// LibraryPage.jsx
// Location: src/pages/LibraryPage.jsx
// Browse all lessons with tabs for Warmups, Grooves & Skills, Practice Lessons

import React, { useState } from 'react';
import { ChevronLeft, Zap, Target, TrendingUp, Clock, Pin, X, Play } from 'lucide-react';
import { warmups, grooves, practiceLessons } from '../data/DrumData.v2';
import GradientHeading from '../components/GradientHeading';

/**
 * Library Page Component
 * Displays all lessons organized into 3 tabs with pin/exclude functionality
 * 
 * @param {Function} onBack - Callback to return to home
 * @param {Array} pinnedItems - Array of pinned item IDs
 * @param {Array} excludedItems - Array of excluded item IDs
 * @param {Function} onTogglePin - Callback when pin button clicked
 * @param {Function} onToggleExclude - Callback when exclude button clicked
 */
const LibraryPage = ({ 
  onBack, 
  pinnedItems, 
  excludedItems, 
  onTogglePin, 
  onToggleExclude 
}) => {
  const [activeTab, setActiveTab] = useState('warmups');

  // Get lessons for current tab
  const getFilteredLessons = () => {
    if (activeTab === 'warmups') return warmups;
    if (activeTab === 'grooves') return grooves;
    if (activeTab === 'practice') return practiceLessons;
    return [];
  };

  const filteredLessons = getFilteredLessons();

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Back
      </button>
      
      {/* Page Header */}
      <GradientHeading size="page" className="mb-3">
       Lesson Library
      </GradientHeading>
      <p className="text-gray-600 mb-6">
        Browse all available warmups, skills, and practice lessons
      </p>
      
      {/* Info Tip */}
      <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-8 flex items-start gap-3">
        <div className="text-purple-600 mt-0.5">💡</div>
        <p className="text-sm text-purple-900">
          <strong>Tip:</strong> Pin items you want in every routine, or exclude items you want to skip. 
          Pinned items will always appear in your daily practice!
        </p>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex gap-3 mb-8">
        {/* Warmups Tab */}
        <button
          onClick={() => setActiveTab('warmups')}
          className={`flex-1 px-6 py-3 rounded-2xl border-2 font-medium transition-all ${
            activeTab === 'warmups'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-200 hover:border-gray-300 text-gray-600'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Zap className="w-5 h-5" />
            Warmups
          </div>
        </button>
        
        {/* Grooves & Skills Tab */}
        <button
          onClick={() => setActiveTab('grooves')}
          className={`flex-1 px-6 py-3 rounded-2xl border-2 font-medium transition-all ${
            activeTab === 'grooves'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-200 hover:border-gray-300 text-gray-600'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Target className="w-5 h-5" />
            Grooves & Skills
          </div>
        </button>
        
        {/* Practice Lessons Tab */}
        <button
          onClick={() => setActiveTab('practice')}
          className={`flex-1 px-6 py-3 rounded-2xl border-2 font-medium transition-all ${
            activeTab === 'practice'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-200 hover:border-gray-300 text-gray-600'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Practice Lessons
          </div>
        </button>
      </div>
      
      {/* Lesson Cards */}
      <div className="space-y-4">
        {filteredLessons.map(lesson => {
          const isPinned = pinnedItems.includes(lesson.id);
          const isExcluded = excludedItems.includes(lesson.id);
          
          return (
            <div 
              key={lesson.id} 
              className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all"
            >
              <div className="flex items-start gap-4">
                {/* Lesson Info */}
                <div className="flex-1">
                  {/* Level Badge (for warmups and practice lessons) */}
                  {(activeTab === 'warmups' || activeTab === 'practice') && lesson.level && (
                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full mb-2">
                      Level {lesson.level}
                    </div>
                  )}
                  
                  {/* Duration */}
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{lesson.duration}</span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {lesson.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 text-sm">
                    {lesson.description || lesson.focus}
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  {/* Pin Button */}
                  <button
                    onClick={() => onTogglePin(lesson.id)}
                    className={`p-2 rounded-xl transition-all ${
                      isPinned
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    title={isPinned ? 'Unpin from routine' : 'Pin to routine'}
                  >
                    <Pin className="w-5 h-5" />
                  </button>
                  
                  {/* Exclude Button */}
                  <button
                    onClick={() => onToggleExclude(lesson.id)}
                    className={`p-2 rounded-xl transition-all ${
                      isExcluded
                        ? 'bg-red-100 text-red-600'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    title={isExcluded ? 'Include in routine' : 'Exclude from routine'}
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  {/* Watch Video Button */}
                  <a
                    href={lesson.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all"
                    title="Watch video"
                  >
                    <Play className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Empty State */}
      {filteredLessons.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No lessons found in this category
        </div>
      )}
    </div>
  );
};

export default LibraryPage;